// ─── Batch-12 FO-002 — Leverancier-status (goedgekeurde/voorkeurs-leveranciers) ─
//
// Eén globale status per relatie-als-leverancier (Client type=LEVERANCIER):
// IN_AFWACHTING -> GOEDGEKEURD -> markering VOORKEUR, plus GEBLOKKEERD (met reden).
// De Zod-schemas zijn de ENE bron: de API parset ermee en de frontend valideert
// ermee (CLAUDE.md "Validatie & foutmeldingen"). Pure gate-logica (welke status
// mag bestellen) staat in packages/utils/src/inkoop-besteltraject.ts.

import { z } from 'zod'
import { idParam } from './id-validators'
import type { LeverancierCategorieTagDTO } from './leverancier-categorie'

type BadgeVariant = 'info' | 'warning' | 'success' | 'danger' | 'secondary'

// ─── Enum (mirror Prisma enum LeverancierStatus) ─────────────────────────────

export const LEVERANCIER_STATUS = ['IN_AFWACHTING', 'GOEDGEKEURD', 'VOORKEUR', 'GEBLOKKEERD'] as const
export type LeverancierStatus = (typeof LEVERANCIER_STATUS)[number]

export const LEVERANCIER_STATUS_LABELS: Record<LeverancierStatus, string> = {
  IN_AFWACHTING: 'In afwachting',
  GOEDGEKEURD: 'Goedgekeurd',
  VOORKEUR: 'Voorkeur',
  GEBLOKKEERD: 'Geblokkeerd',
}

export const LEVERANCIER_STATUS_BADGE_VARIANT: Record<LeverancierStatus, BadgeVariant> = {
  IN_AFWACHTING: 'warning',
  GOEDGEKEURD: 'info',
  VOORKEUR: 'success',
  GEBLOKKEERD: 'danger',
}

/**
 * Bestelbaar = er mag (na status-controle) besteld worden bij deze leverancier.
 * GOEDGEKEURD en VOORKEUR zijn bestelbaar; IN_AFWACHTING en GEBLOKKEERD niet.
 * De server-side gate (bepaalLeverancierBestelGate in @streep/utils) is leidend;
 * deze helper is voor UI-weergave (label/sortering/disabled-state).
 */
export function isLeverancierBestelbaar(status: LeverancierStatus | null | undefined): boolean {
  return status === 'GOEDGEKEURD' || status === 'VOORKEUR'
}

// ─── DTO — leverancier-overzicht + selector ──────────────────────────────────

export interface InkoopLeverancierListItemDTO {
  id: string
  name: string
  clientNumber: string
  city: string | null
  email: string | null
  phone: string | null
  /** Leverancier-status; null = (legacy) onbekend, behandeld als IN_AFWACHTING. */
  status: LeverancierStatus | null
  /** Reden bij GEBLOKKEERD (zichtbaar bij leverancier én bij de keuze). */
  blokkadeReden: string | null
  /** Afgeleid: mag er besteld worden (GOEDGEKEURD/VOORKEUR). */
  bestelbaar: boolean
  /** Afgeleid: status === VOORKEUR (voor "voorkeur bovenaan" + label). */
  voorkeur: boolean
  statusGewijzigdOp: string | null
  statusDoorNaam: string | null
  /** Mini-historie voor context bij de keuze (aantal orders + laatste bestelling). */
  aantalOrders: number
  laatsteBestellingOp: string | null
  /** Leverancier-categorieen (type) met de voorkeur-rang in die categorie. */
  categorieen: LeverancierCategorieTagDTO[]
}

// ─── Zod-schemas ─────────────────────────────────────────────────────────────

/** Blokkeren vereist een reden (FO-002 §6). */
export const leverancierBlokkerenSchema = z.object({
  reden: z.string().trim().min(3, 'Geef een reden voor de blokkade (minimaal 3 tekens)').max(2000),
})
export type LeverancierBlokkerenInput = z.infer<typeof leverancierBlokkerenSchema>

/**
 * Portal: een monteur draagt een nog onbekende leverancier aan. Levert een
 * concept-leverancier (status IN_AFWACHTING) op die inkoop beoordeelt.
 */
export const leverancierVoorstelSchema = z.object({
  naam: z.string().trim().min(2, 'Vul de naam van de leverancier in').max(200),
  kvkNummer: z.string().trim().max(20).optional().nullable(),
  website: z.string().trim().max(300).optional().nullable(),
  toelichting: z.string().trim().max(2000).optional().nullable(),
})
export type LeverancierVoorstelInput = z.infer<typeof leverancierVoorstelSchema>

/** Lijst-/selector-query voor GET /inkoop/leveranciers. */
export const inkoopLeverancierListQuerySchema = z.object({
  status: z.enum(LEVERANCIER_STATUS).optional(),
  // Alleen bestelbare leveranciers (GOEDGEKEURD/VOORKEUR) — voor de selector.
  bestelbaar: z.enum(['true', 'false']).optional(),
  search: z.string().trim().optional(),
  // Categorie-filter; 'GEEN' = leveranciers zonder categorie.
  categorieId: z.union([z.literal('GEEN'), idParam()]).optional(),
})
export type InkoopLeverancierListQuery = z.infer<typeof inkoopLeverancierListQuerySchema>

/** Voorkeursleverancier-keuze in de portal-aanvraag (alleen bestelbare). */
export const portalLeverancierKeuzeSchema = z.object({
  voorkeurLeverancierId: idParam().optional().nullable(),
})
